import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {AttachmentData} from '@docma-mm-core/attachment/attachment';
import {BehaviorSubject} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {MmAddAttachmentsFormService} from './mm-add-attachments.form.service';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {PixService} from './pix.service';
import {FSSelectFileStrategy} from '@mydocma/component-library/src/lib/drag-drop-file-upload';
import {AndroidFileChoiceStrategy} from './android-file-strategy';
import {MatDialog} from '@angular/material/dialog';
import { NativeService } from '@mydocma/core/src/lib/native';
import { PlatformModeStateService } from '../../mm-offline/services/platform-mode-state.service';
import { constants } from '@docma-mm-core/constants';

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-add-attachments-form',
  templateUrl: './mm-add-attachments-form.component.html',
  styleUrls: ['./mm-add-attachments-form.component.scss'],
  providers: [MmAddAttachmentsFormService]
})
export class MmAddAttachmentsFormComponent implements OnInit, OnChanges, OnDestroy {

  constructor(public readonly addAttachmentsFormService: MmAddAttachmentsFormService,
              private deviceService: DeviceService,
              private pixService: PixService,
              private dialog: MatDialog,
              private nativeService: NativeService,
              private platformModeStateService: PlatformModeStateService) {

    this.loadForm();
  }

  // Store pending PixFile metadata if form service is not ready
  private pendingPixFileFormValues: any = null;

  @Input()
  attachmentData = new AttachmentData();

  @Input()
  filesIn: File[];

  filesChangedSubject: BehaviorSubject<File[]> = new BehaviorSubject<File[]>(null);
  filesChanged$ = this.filesChangedSubject.asObservable();

  descriptionExpanded = false;
  files = [];
  acceptedTypes: Array<string>;
  attachmentsForm: UntypedFormGroup;

  mobileMode = false;
  fileStrategies: any[] = [];

  onFormReady(event: any) {
    // Store the form ready event in the service for value access
    this.addAttachmentsFormService.onReady(event);
    const formService = event?.dynamicFormService;
    if (formService) {
      if (this.files.length === 1) {
        const file = this.files[0];
        if (this.hasPixMeta(file)) {
          formService.setFormValue(this.pixFormValues(file));
        } else if (this.addAttachmentsFormService.description || this.addAttachmentsFormService.comment || this.addAttachmentsFormService.documentType) {
          const formValues = {
            description: this.addAttachmentsFormService.description,
            comment: this.addAttachmentsFormService.comment,
            type: this.addAttachmentsFormService.documentType
          };
          formService.setFormValue(formValues);
        }
      } else {
        // For multiple files, clear form fields after change detection
        setTimeout(() => {
          const blankValues = {
            description: null,
            comment: null,
            type: null
          };
          formService.setFormValue(blankValues);
        });
      }
      // If there are pending PixFile values, set them now (only for single file)
      if (this.pendingPixFileFormValues && this.files.length === 1) {
        formService.setFormValue(this.pendingPixFileFormValues);
        this.pendingPixFileFormValues = null;
      }
    }
  }

  ngOnDestroy(): void {
  }

  ngOnInit() {
    this.mobileMode = !this.deviceService.isDesktop();

    if (this.platformModeStateService.isLocalModeEnabled && this.nativeService.isIos){
      this.acceptedTypes = constants.nativeAcceptTypes;
    }

    const deviceInfo = this.deviceService.getDeviceInfo();
    const isAndroidMobile = this.mobileMode && deviceInfo?.os?.toLowerCase() === 'android';

    if (isAndroidMobile) {
      this.fileStrategies = [new AndroidFileChoiceStrategy(this.dialog, this.nativeService)];
    } else {
      this.fileStrategies = [new FSSelectFileStrategy()];
    }

    this.pixService.ensureInit();
    this.pixService.availableProjects$
      .pipe(untilDestroyed(this))
      .subscribe(projects => {
        const hasAny = !!projects && projects.length > 0;
        this.togglePixStrategy(hasAny);
      });
  }

  private togglePixStrategy(shouldInclude: boolean) {
    const pix = this.pixService.pixFSStrategy;
    const idx = this.fileStrategies.findIndex(s => s && s.getFiles === pix.getFiles);

    if (shouldInclude) {
      if (idx === -1) {
        this.fileStrategies = [...this.fileStrategies, pix];
      }
    } else {
      if (idx !== -1) {
        const next = [...this.fileStrategies];
        next.splice(idx, 1);
        this.fileStrategies = next;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.attachmentData) {
      this.addAttachmentsFormService.loadAttachment(changes.attachmentData as AttachmentData);
      this.loadForm();
    }

    if (changes.filesIn) {
      const files: File[] = this.filesIn;
      this.onFilesAdded(files);
    }
  }

  private loadForm() {
    this.addAttachmentsFormService.loadAttachment(this.attachmentData);
    this.files = [];
    this.attachmentsForm = new UntypedFormGroup({
      files: new UntypedFormControl(null, Validators.required)
    });
  }

  reset() {
    this.attachmentData = new AttachmentData();
    this.addAttachmentsFormService.loadAttachment(this.attachmentData);
    this.loadForm();
  }

  validate(): boolean {
    if (!this.attachmentsForm.valid) {
      this.attachmentsForm.markAllAsTouched();
      return false;
    }
    return true;
  }

  isChanged() {
    return (this.attachmentsForm.dirty || this.attachmentsForm.pristine);
  }

  submit(): { attachmentData: AttachmentData, files: File[] } {
    if (!this.validate()) {
      return null;
    }
    let attachmentData: AttachmentData;
    if (this.files.length === 1) {
      attachmentData = this.addAttachmentsFormService.getChanges(this.files);
    } else {
      // For multiple files, return blank attachment
      attachmentData = new AttachmentData();
      attachmentData.Description = null;
      attachmentData.Comment = null;
      attachmentData.DocumentType = null;
      attachmentData.FileName = null;
      attachmentData.OriginalFileName = null;
    }
    this.addAttachmentsFormService.discard();

    return {attachmentData, files: this.files};
  }

  onFileDeleted(event: any) {
    this.files.splice(event.index, 1);
    this.files = [...this.files];
    if (this.files.length === 0) {
      this.attachmentsForm.patchValue({files: null});
    } else {
      this.attachmentsForm.patchValue({files: this.files});
      // If only one file remains and it is a PixFile, populate form fields from its metadata
      if (this.files.length === 1) {
        const file = this.files[0];
        if (this.hasPixMeta(file)) {
          const formService = this.addAttachmentsFormService.formReadyEvent?.dynamicFormService;
          const formValues = this.pixFormValues(file);
          if (formService) {
            formService.setFormValue(formValues);
          } else {
            this.pendingPixFileFormValues = formValues;
          }
        }
      }
    }
    this.filesChangedSubject.next(this.files);
  }

  onFilesAdded(fileList: File[]) {
    if (!fileList) {
      return;
    }
    this.files = [...this.files];
    this.files.push(...fileList);
    this.attachmentsForm.patchValue({files: this.files});
    this.filesChangedSubject.next(this.files);

    // Fill form fields with PixFile metadata if single PixFile, else clear fields for multi-file
    const formService = this.addAttachmentsFormService.formReadyEvent?.dynamicFormService;
    if (formService) {
      if (this.files.length === 1) {
        const file = this.files[0];
        if (this.hasPixMeta(file)) {
          formService.setFormValue(this.pixFormValues(file));
        }
      } else {
        // For multiple files, clear form fields after change detection
        setTimeout(() => {
          const blankValues = {
            description: null,
            comment: null,
            type: null
          };
          formService.setFormValue(blankValues);
        });
      }
    }
  }

  private hasPixMeta(file: any): boolean {
    return !!(file?.title || file?.desc);
  }

  private pixFormValues(file: any) {
    return {
      description: file?.title ?? null,
      comment: file?.desc ?? null,
      type: null
    };
  }
}
